export type AffirmationCategory =
  | 'Confidence'
  | 'Self-Worth'
  | 'Emotional Wellbeing'
  | 'Safety & Security'
  | 'Friendships'
  | 'Body Safety'
  // New categories (Task 4)
  | 'Navigating Emotions'
  | 'Body Safety & Personal Boundaries'
  | 'Online Safety & Digital Life'
  | 'Friendships & Social Skills'
  | 'Dealing with Conflict'
  | 'Resilience & Growth Mindset'
  | 'Healthy Habits'
  | 'Gratitude & Mindfulness'
  | 'Money & Responsibility'
  | 'Community & Kindness'
  | 'Family & Relationships'
  | 'Identity & Self-Expression';

export interface Affirmation {
  id: string;
  text: string;
  ageGroups: Array<'4-6' | '7-9' | '10-12'>;
  category: AffirmationCategory;
  parentPrompt: string;
  isPremium: boolean;
}

export const AFFIRMATION_CATEGORIES: AffirmationCategory[] = [
  'Confidence',
  'Self-Worth',
  'Emotional Wellbeing',
  'Safety & Security',
  'Friendships',
  'Body Safety',
  // New categories (Task 4)
  'Navigating Emotions',
  'Body Safety & Personal Boundaries',
  'Online Safety & Digital Life',
  'Friendships & Social Skills',
  'Dealing with Conflict',
  'Resilience & Growth Mindset',
  'Healthy Habits',
  'Gratitude & Mindfulness',
  'Money & Responsibility',
  'Community & Kindness',
  'Family & Relationships',
  'Identity & Self-Expression',
];

export const FREE_SAMPLE_COUNT = 3;

export const AFFIRMATIONS: Affirmation[] = [
  {
    id: 'aff-001',
    text: 'I am safe and loved.',
    ageGroups: ['4-6', '7-9'],
    category: 'Body Safety & Personal Boundaries',
    parentPrompt: "Say this together at bedtime. Ask: 'Who are the people who keep you safe?'",
    isPremium: true,
  },
  {
    id: 'aff-002',
    text: 'My feelings are important and it is okay to talk about them.',
    ageGroups: ['4-6', '7-9'],
    category: 'Navigating Emotions',
    parentPrompt: "Ask: 'What's one feeling you had today?' Validate whatever they share.",
    isPremium: true,
  },
  {
    id: 'aff-003',
    text: 'I am brave enough to try new things.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Resilience & Growth Mindset',
    parentPrompt: "Ask: 'What's something new you'd like to try?' Encourage without pressure.",
    isPremium: true,
  },
  {
    id: 'aff-004',
    text: 'My body belongs to me.',
    ageGroups: ['4-6', '7-9'],
    category: 'Body Safety & Personal Boundaries',
    parentPrompt: "Reinforce: 'You are always in charge of your own body. No one can touch you without your permission.'",
    isPremium: true,
  },
  {
    id: 'aff-005',
    text: 'I can ask for help whenever I need it.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Resilience & Growth Mindset',
    parentPrompt: "Ask: 'Who are the grown-ups you can always go to for help?' Name them together.",
    isPremium: true,
  },
  {
    id: 'aff-006',
    text: 'I am kind to others and kind to myself.',
    ageGroups: ['4-6', '7-9'],
    category: 'Community & Kindness',
    parentPrompt: "Ask: 'What kind thing did you do for someone today? What kind thing did you do for yourself?'",
    isPremium: true,
  },
  {
    id: 'aff-007',
    text: 'Making mistakes helps me learn and grow.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Resilience & Growth Mindset',
    parentPrompt: 'Share a mistake you made this week and what you learned. Model that mistakes are normal.',
    isPremium: true,
  },
  {
    id: 'aff-008',
    text: 'I am enough, exactly as I am.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Self-Worth',
    parentPrompt: "Say: 'There is nothing you need to do or be to earn my love. You are enough right now.'",
    isPremium: true,
  },
  {
    id: 'aff-009',
    text: 'I know how to say no when something feels wrong.',
    ageGroups: ['4-6', '7-9'],
    category: 'Body Safety & Personal Boundaries',
    parentPrompt: "Practise saying 'No!' together — loudly and clearly. Make it fun, do it three times with increasing volume. Then talk about what to do after saying no.",
    isPremium: true,
  },
  {
    id: 'aff-010',
    text: 'I have people who love me and will always help me.',
    ageGroups: ['4-6', '7-9'],
    category: 'Family & Relationships',
    parentPrompt: "Name three people together who love your child unconditionally. Let them feel the weight of that.",
    isPremium: true,
  },
  {
    id: 'aff-011',
    text: 'I am a good friend.',
    ageGroups: ['4-6', '7-9'],
    category: 'Friendships & Social Skills',
    parentPrompt: "Ask: 'What makes you a good friend? What's something kind you did for a friend this week?'",
    isPremium: true,
  },
  {
    id: 'aff-012',
    text: 'I can stay calm when things feel hard.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Navigating Emotions',
    parentPrompt: 'Practise one breathing exercise together: breathe in for 4 counts, out for 4. Do it three times.',
    isPremium: true,
  },
  {
    id: 'aff-013',
    text: "It is okay to feel scared. Scared feelings don't last forever.",
    ageGroups: ['4-6', '7-9'],
    category: 'Navigating Emotions',
    parentPrompt: "Ask: 'What's something that sometimes makes you feel scared?' Validate fully before reassuring.",
    isPremium: true,
  },
  {
    id: 'aff-014',
    text: 'I am curious and I love to learn.',
    ageGroups: ['4-6', '7-9'],
    category: 'Confidence',
    parentPrompt: "Ask: 'What's something you want to know more about?' Follow their lead with genuine curiosity.",
    isPremium: true,
  },
  {
    id: 'aff-015',
    text: 'My voice matters. What I say and feel matters.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Identity & Self-Expression',
    parentPrompt: 'Give your child a moment to speak uninterrupted about something they care about. Just listen.',
    isPremium: true,
  },
  {
    id: 'aff-016',
    text: 'I am strong — in my body and in my mind.',
    ageGroups: ['4-6', '7-9'],
    category: 'Confidence',
    parentPrompt: "Ask: 'What's something your body can do that you're proud of? What's something your brain can do?'",
    isPremium: true,
  },
  {
    id: 'aff-017',
    text: 'I treat other people the way I want to be treated.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Friendships',
    parentPrompt: "Ask: 'How did you treat someone today? How did it make them feel? How did it make you feel?'",
    isPremium: true,
  },
  {
    id: 'aff-018',
    text: 'I am proud of myself for trying, even when it is hard.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Confidence',
    parentPrompt: "Name one thing your child tried hard at recently and tell them specifically why you're proud of the effort, not just the outcome.",
    isPremium: true,
  },
  {
    id: 'aff-019',
    text: 'I can tell a trusted grown-up anything — I will never be in trouble for telling the truth.',
    ageGroups: ['4-6', '7-9'],
    category: 'Body Safety & Personal Boundaries',
    parentPrompt: "Say directly: 'There is nothing you could tell me that would make me stop loving you. I will always listen.'",
    isPremium: true,
  },
  {
    id: 'aff-020',
    text: 'I notice when something feels wrong, and I trust that feeling.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Body Safety',
    parentPrompt: "Ask: 'Can you describe what your tummy feels like when something feels a bit wrong or unsafe?'",
    isPremium: true,
  },
  {
    id: 'aff-021',
    text: 'I am loved for who I am, not what I do.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Self-Worth',
    parentPrompt: "Say: 'Even on your hardest day, even when you get things wrong — I love you completely. That never changes.'",
    isPremium: true,
  },
  {
    id: 'aff-022',
    text: 'I can solve problems by thinking and asking for help.',
    ageGroups: ['4-6', '7-9'],
    category: 'Confidence',
    parentPrompt: "Next time a small problem comes up, resist solving it for them. Ask: 'What do you think we could try?'",
    isPremium: true,
  },
  {
    id: 'aff-023',
    text: 'My home is a safe place.',
    ageGroups: ['4-6', '7-9'],
    category: 'Safety & Security',
    parentPrompt: "Ask: 'What makes you feel most safe and cosy at home?' Let them name what matters to them.",
    isPremium: true,
  },
  {
    id: 'aff-024',
    text: 'I am allowed to change my mind.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Confidence',
    parentPrompt: "Model this yourself: share a time you changed your mind about something and why. Normalise it.",
    isPremium: true,
  },
  {
    id: 'aff-025',
    text: 'I know who to call if I ever feel unsafe.',
    ageGroups: ['4-6', '7-9'],
    category: 'Safety & Security',
    parentPrompt: "Quiz them gently: 'If something felt wrong and I wasn't there, who would you call? What's our phone number?'",
    isPremium: true,
  },
  {
    id: 'aff-026',
    text: 'There are no secrets about my body that I need to keep.',
    ageGroups: ['4-6', '7-9'],
    category: 'Body Safety',
    parentPrompt: "Reinforce: 'If anyone ever asks you to keep a secret about your body — you always tell me. Always. You will never be in trouble.'",
    isPremium: true,
  },
  {
    id: 'aff-027',
    text: 'I am unique and that is my superpower.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Self-Worth',
    parentPrompt: "Name one specific thing that makes your child uniquely themselves — something they might not have heard named before.",
    isPremium: true,
  },
  {
    id: 'aff-028',
    text: "I can be friends with lots of different people.",
    ageGroups: ['4-6', '7-9'],
    category: 'Friendships',
    parentPrompt: "Ask: 'Is there someone at school you haven't talked to much? What might you have in common with them?'",
    isPremium: true,
  },
  {
    id: 'aff-029',
    text: 'When I feel angry, I can find a safe way to let it out.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Emotional Wellbeing',
    parentPrompt: "Create an 'anger toolkit' together: three things they can do when they feel very angry (run, draw, squeeze a pillow, tell you).",
    isPremium: true,
  },
  {
    id: 'aff-030',
    text: 'I am responsible and people can trust me.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Confidence',
    parentPrompt: "Give your child a small responsibility this week that's just for them. Trust them with it fully.",
    isPremium: true,
  },
  {
    id: 'aff-031',
    text: 'I am a good listener.',
    ageGroups: ['4-6', '7-9'],
    category: 'Friendships',
    parentPrompt: 'Play the listening game: one person speaks for one minute about something they love, the other only listens. Then swap.',
    isPremium: true,
  },
  {
    id: 'aff-032',
    text: "I don't have to be perfect to be wonderful.",
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Self-Worth',
    parentPrompt: "Ask: 'Is there something you've been hard on yourself about lately?' Listen — don't rush to fix it.",
    isPremium: true,
  },
  {
    id: 'aff-033',
    text: 'I can be honest even when it is difficult.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Confidence',
    parentPrompt: "Share a time you told a hard truth and it turned out to be the right thing. Model honesty as something to be proud of.",
    isPremium: true,
  },
  {
    id: 'aff-034',
    text: 'I matter.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Self-Worth',
    parentPrompt: "Say this one slowly. Then tell your child three specific reasons why they matter — to you, and to the world.",
    isPremium: true,
  },
  {
    id: 'aff-035',
    text: 'Tomorrow is a fresh start.',
    ageGroups: ['4-6', '7-9', '10-12'],
    category: 'Emotional Wellbeing',
    parentPrompt: "Use this at bedtime after a hard day. Ask: 'What's one thing you're going to leave behind tonight? What's one thing you're looking forward to tomorrow?'",
    isPremium: true,
  },
  {
    id: 'aff-036',
    text: 'I am in charge of my own choices.',
    ageGroups: ['7-9', '10-12'],
    category: 'Confidence',
    parentPrompt: "Discuss: 'What's a choice you made this week that you felt good about? What made it feel right?'",
    isPremium: true,
  },
  {
    id: 'aff-037',
    text: 'I can stand up for myself — calmly and clearly.',
    ageGroups: ['7-9', '10-12'],
    category: 'Confidence',
    parentPrompt: "Roleplay a situation where someone is being unkind. Practise responding assertively — not aggressively, not silently.",
    isPremium: true,
  },
  {
    id: 'aff-038',
    text: "I am not responsible for other people's bad behaviour.",
    ageGroups: ['7-9', '10-12'],
    category: 'Dealing with Conflict',
    parentPrompt: "Ask: 'Is there anything happening with friends or at school that you've been blaming yourself for?' Listen carefully.",
    isPremium: true,
  },
  {
    id: 'aff-039',
    text: 'I choose my friends carefully and treat them well.',
    ageGroups: ['7-9', '10-12'],
    category: 'Friendships & Social Skills',
    parentPrompt: "Ask: 'What are the most important qualities in a good friend?' Then: 'Do the people you spend time with have those qualities?'",
    isPremium: true,
  },
  {
    id: 'aff-040',
    text: 'I know the difference between a kind secret and an unsafe secret.',
    ageGroups: ['7-9'],
    category: 'Body Safety & Personal Boundaries',
    parentPrompt: "Revisit the rule: surprise secrets (like presents) are okay for a short time. Secrets about bodies or feelings of fear — always tell a parent.",
    isPremium: true,
  },
  {
    id: 'aff-041',
    text: 'Being different from others is something to be proud of.',
    ageGroups: ['7-9', '10-12'],
    category: 'Self-Worth',
    parentPrompt: "Ask: 'Is there something about you that feels different from your friends? How do you feel about that?'",
    isPremium: true,
  },
  {
    id: 'aff-042',
    text: 'I can disagree with someone and still be kind.',
    ageGroups: ['7-9', '10-12'],
    category: 'Dealing with Conflict',
    parentPrompt: "Practise respectful disagreement: 'I see it differently — I think...' Show that disagreement and respect can coexist.",
    isPremium: true,
  },
  {
    id: 'aff-043',
    text: 'It is brave to ask for help — not weak.',
    ageGroups: ['7-9', '10-12'],
    category: 'Resilience & Growth Mindset',
    parentPrompt: 'Share a time you asked for help as an adult. Normalise it as something strong people do.',
    isPremium: true,
  },
  {
    id: 'aff-044',
    text: 'I notice when my friends might be struggling, and I check in on them.',
    ageGroups: ['7-9', '10-12'],
    category: 'Community & Kindness',
    parentPrompt: "Ask: 'Is there a friend who seems a bit unlike themselves lately? What could you do to check in on them?'",
    isPremium: true,
  },
  {
    id: 'aff-045',
    text: 'Online or offline — I am the same person with the same values.',
    ageGroups: ['7-9', '10-12'],
    category: 'Online Safety & Digital Life',
    parentPrompt: "Discuss: 'Would you say the things you say online to someone's face? Should there be a difference?'",
    isPremium: true,
  },
  {
    id: 'aff-046',
    text: 'I don\'t need to pretend to be someone I am not.',
    ageGroups: ['7-9', '10-12'],
    category: 'Self-Worth',
    parentPrompt: "Ask: 'Is there anywhere in your life where you feel like you have to act differently to fit in? How does that feel?'",
    isPremium: true,
  },
  {
    id: 'aff-047',
    text: 'My mental health matters as much as my physical health.',
    ageGroups: ['7-9', '10-12'],
    category: 'Emotional Wellbeing',
    parentPrompt: "Normalise: 'Just like we go to the doctor for a sore throat, we can get help when our feelings feel too big to handle alone.'",
    isPremium: true,
  },
  {
    id: 'aff-048',
    text: 'I can walk away from situations that don\'t feel right.',
    ageGroups: ['7-9', '10-12'],
    category: 'Body Safety & Personal Boundaries',
    parentPrompt: "Reinforce: 'You never need to stay somewhere that makes you uncomfortable — even if leaving feels awkward. I will always come and get you.'",
    isPremium: true,
  },
  {
    id: 'aff-049',
    text: 'The people who matter will like me for who I really am.',
    ageGroups: ['7-9', '10-12'],
    category: 'Self-Worth',
    parentPrompt: "Ask: 'Are there people in your life you feel completely yourself around? What makes that possible?'",
    isPremium: true,
  },
  {
    id: 'aff-050',
    text: 'I deserve to feel safe everywhere I go.',
    ageGroups: ['7-9', '10-12'],
    category: 'Body Safety & Personal Boundaries',
    parentPrompt: "Ask: 'Is there anywhere — at school, online, anywhere — where you don't feel completely safe?' Listen with full attention.",
    isPremium: true,
  },
  {
    id: 'aff-051',
    text: 'I face hard things and come out stronger.',
    ageGroups: ['7-9', '10-12'],
    category: 'Resilience & Growth Mindset',
    parentPrompt: "Name a specific challenge your child has already overcome. Remind them: 'You got through that. That's real strength.'",
    isPremium: true,
  },
  {
    id: 'aff-052',
    text: 'I am not my worst moment.',
    ageGroups: ['7-9', '10-12'],
    category: 'Navigating Emotions',
    parentPrompt: "Share: 'Everyone has moments they're not proud of. What matters is what you do next — not that it happened.'",
    isPremium: true,
  },
  {
    id: 'aff-053',
    text: 'Saying no to something that doesn\'t feel right is an act of self-respect.',
    ageGroups: ['7-9', '10-12'],
    category: 'Body Safety & Personal Boundaries',
    parentPrompt: "Discuss: 'Can you think of a situation where saying no was the brave and right thing? What made it hard?'",
    isPremium: true,
  },
  {
    id: 'aff-054',
    text: 'I can talk to a trusted adult about anything — including things that feel embarrassing.',
    ageGroups: ['7-9', '10-12'],
    category: 'Body Safety & Personal Boundaries',
    parentPrompt: "Tell your child directly: 'There is nothing so embarrassing that you couldn't come to me. I've heard it all and I love you regardless.'",
    isPremium: true,
  },
  {
    id: 'aff-055',
    text: 'I am more than how I look.',
    ageGroups: ['7-9', '10-12'],
    category: 'Self-Worth',
    parentPrompt: 'Name five things you love about your child that have nothing to do with their appearance. Be specific.',
    isPremium: true,
  },
  {
    id: 'aff-056',
    text: "I choose kindness — even when others don't.",
    ageGroups: ['7-9', '10-12'],
    category: 'Community & Kindness',
    parentPrompt: "Ask: 'Can you think of a time you were kind to someone who wasn't being kind back? What made you do that?'",
    isPremium: true,
  },
  {
    id: 'aff-057',
    text: 'I am growing and changing, and that is a good thing.',
    ageGroups: ['7-9', '10-12'],
    category: 'Resilience & Growth Mindset',
    parentPrompt: "Share one way you've seen your child grow this year — something specific about their character, not their grades or achievements.",
    isPremium: true,
  },
  {
    id: 'aff-058',
    text: 'I set my own standards — I don\'t need to compare myself to others.',
    ageGroups: ['7-9', '10-12'],
    category: 'Identity & Self-Expression',
    parentPrompt: "Discuss social comparison — at school and online. Ask: 'When do you find yourself comparing yourself to others? How does it make you feel?'",
    isPremium: true,
  },
  {
    id: 'aff-059',
    text: 'Pressure from friends doesn\'t have to change what I know is right.',
    ageGroups: ['7-9', '10-12'],
    category: 'Confidence',
    parentPrompt: "Roleplay a peer pressure scenario. Practise saying: 'I'm not doing that' — without lengthy explanation, without apology.",
    isPremium: true,
  },
  {
    id: 'aff-060',
    text: 'I take care of my mind the same way I take care of my body.',
    ageGroups: ['7-9', '10-12'],
    category: 'Healthy Habits',
    parentPrompt: "Create a simple 'mind care' list together: things that help them feel mentally well (sleep, movement, time outside, talking, creating).",
    isPremium: true,
  },
  {
    id: 'aff-061',
    text: "I know who I am, and I don't need others to define me.",
    ageGroups: ['10-12'],
    category: 'Identity & Self-Expression',
    parentPrompt: "Ask: 'How would you describe yourself — not your hobbies or school, but who you actually are as a person?'",
    isPremium: true,
  },
  {
    id: 'aff-062',
    text: 'My worth is not determined by likes, followers, or what others post about me.',
    ageGroups: ['10-12'],
    category: 'Online Safety & Digital Life',
    parentPrompt: "Discuss: 'How do you feel after spending time on social media? Better, worse, or the same? What does that tell you?'",
    isPremium: true,
  },
  {
    id: 'aff-063',
    text: 'I can feel anxious and still do the thing.',
    ageGroups: ['10-12'],
    category: 'Confidence',
    parentPrompt: 'Name something your child did recently while feeling scared or nervous. Recognise the bravery explicitly.',
    isPremium: true,
  },
  {
    id: 'aff-064',
    text: "I don't have to manage other people's emotions for them.",
    ageGroups: ['10-12'],
    category: 'Navigating Emotions',
    parentPrompt: "Ask: 'Is there anyone whose feelings you feel responsible for? What would it mean to let that go a little?'",
    isPremium: true,
  },
  {
    id: 'aff-065',
    text: 'I protect my energy and choose what I give my attention to.',
    ageGroups: ['10-12'],
    category: 'Healthy Habits',
    parentPrompt: "Discuss: 'What things in your life drain you? What fills you back up? Are you spending more time on one than the other?'",
    isPremium: true,
  },
  {
    id: 'aff-066',
    text: 'Real friendship is built on trust, honesty, and respect.',
    ageGroups: ['10-12'],
    category: 'Friendships & Social Skills',
    parentPrompt: "Ask: 'Do your closest friendships have those three things? Is there one that's missing in any of them?'",
    isPremium: true,
  },
  {
    id: 'aff-067',
    text: 'I don\'t need to shrink myself to make others comfortable.',
    ageGroups: ['10-12'],
    category: 'Identity & Self-Expression',
    parentPrompt: "Ask: 'Is there anywhere in your life where you feel like you hold back who you really are? What makes you do that?'",
    isPremium: true,
  },
  {
    id: 'aff-068',
    text: 'My mistakes do not define my future.',
    ageGroups: ['10-12'],
    category: 'Confidence',
    parentPrompt: 'Share a mistake from your own past and what became possible after it. Show that setbacks are rarely permanent.',
    isPremium: true,
  },
  {
    id: 'aff-069',
    text: 'I have the right to privacy — including from people I love.',
    ageGroups: ['10-12'],
    category: 'Confidence',
    parentPrompt: 'Discuss the difference between healthy privacy (your own thoughts, diary, space) and secrecy that puts you at risk. Both are real.',
    isPremium: true,
  },
  {
    id: 'aff-070',
    text: 'Asking for help when I\'m struggling is one of the smartest things I can do.',
    ageGroups: ['10-12'],
    category: 'Emotional Wellbeing',
    parentPrompt: "Name a professional or trusted adult (beyond yourself) your child could speak to if they needed support. Make sure they know this person and trust them.",
    isPremium: true,
  },
  {
    id: 'aff-071',
    text: "I think critically — I don't just believe everything I see online.",
    ageGroups: ['10-12'],
    category: 'Online Safety & Digital Life',
    parentPrompt: "Find a misleading post or headline together and analyse it: 'What does it want us to feel? What might it be leaving out?'",
    isPremium: true,
  },
  {
    id: 'aff-072',
    text: 'My past does not have to repeat itself.',
    ageGroups: ['10-12'],
    category: 'Resilience & Growth Mindset',
    parentPrompt: "This one is about agency. Ask: 'Is there a pattern in your life you'd like to change? What's one small thing that could be different?'",
    isPremium: true,
  },
  {
    id: 'aff-073',
    text: "I speak up when something isn't right — even when it's hard.",
    ageGroups: ['10-12'],
    category: 'Confidence',
    parentPrompt: "Discuss: 'Is there something you've witnessed recently that didn't feel right but you weren't sure whether to say something?'",
    isPremium: true,
  },
  {
    id: 'aff-074',
    text: 'I am allowed to outgrow friendships that no longer feel good.',
    ageGroups: ['10-12'],
    category: 'Friendships & Social Skills',
    parentPrompt: "Normalise this gently: 'As we grow, not all friendships grow with us. That's okay — it doesn't mean anyone did anything wrong.'",
    isPremium: true,
  },
  {
    id: 'aff-075',
    text: 'I take responsibility for my actions without tearing myself apart.',
    ageGroups: ['10-12'],
    category: 'Self-Worth',
    parentPrompt: 'Discuss the difference between accountability (owning what you did, making it right, moving forward) and shame (believing you are bad). Model the former.',
    isPremium: true,
  },
  {
    id: 'aff-076',
    text: 'I am building a life that feels like mine.',
    ageGroups: ['10-12'],
    category: 'Confidence',
    parentPrompt: "Ask: 'What kind of person do you want to be? Not what job — who. What qualities matter to you?' Listen without steering.",
    isPremium: true,
  },
  {
    id: 'aff-077',
    text: 'Consent is something I give and expect in return — always.',
    ageGroups: ['10-12'],
    category: 'Body Safety & Personal Boundaries',
    parentPrompt: "Discuss: 'What does consent mean to you, beyond just physical contact? Can you think of examples where it matters in everyday life?'",
    isPremium: true,
  },
  {
    id: 'aff-078',
    text: 'The pressure I feel is real — and so is my ability to handle it.',
    ageGroups: ['10-12'],
    category: 'Navigating Emotions',
    parentPrompt: "Ask: 'What's the biggest source of pressure in your life right now?' Acknowledge it fully before problem-solving, if at all.",
    isPremium: true,
  },
  {
    id: 'aff-079',
    text: 'I trust myself.',
    ageGroups: ['10-12'],
    category: 'Confidence',
    parentPrompt: "Ask: 'When do you trust yourself the most? When is it hardest to trust yourself? What makes the difference?'",
    isPremium: true,
  },
  {
    id: 'aff-080',
    text: 'I am enough for the right people.',
    ageGroups: ['10-12'],
    category: 'Self-Worth',
    parentPrompt: "Say: 'The people who deserve you will never make you feel like too much or not enough. Hold onto that standard for every relationship in your life.'",
    isPremium: true,
  },
];

export function getAffirmationsForAgeGroup(ageGroup: '4-6' | '7-9' | '10-12'): Affirmation[] {
  return AFFIRMATIONS.filter((a) => a.ageGroups.includes(ageGroup));
}

export function getDailyAffirmation(ageGroups: Array<'4-6' | '7-9' | '10-12'>): Affirmation {
  const pool =
    ageGroups.length > 0
      ? AFFIRMATIONS.filter((a) => ageGroups.some((ag) => a.ageGroups.includes(ag)))
      : AFFIRMATIONS;
  const affPool = pool.length > 0 ? pool : AFFIRMATIONS;
  const dayOfYear = Math.floor(
    (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000
  );
  return affPool[dayOfYear % affPool.length];
}

// ── Guest / free static affirmation ──────────────────────────────────────────
export const GUEST_AFFIRMATION_TEXT =
  "I can tell a trusted grown-up anything \u2014 I will never be in trouble for telling the truth.";

// Matches the existing aff-019 record so categories + parentPrompt are available
export const GUEST_STATIC_AFFIRMATION: Affirmation = {
  id: 'aff-019',
  text: GUEST_AFFIRMATION_TEXT,
  ageGroups: ['4-6', '7-9'],
  category: 'Body Safety & Personal Boundaries',
  parentPrompt:
    "Say directly: \u2018There is nothing you could tell me that would make me stop loving you. I will always listen.\u2019",
  isPremium: false,
};

// ── Premium daily rotation pool (30 starters) ────────────────────────────────
// TODO: expand to 365 affirmations
export const PREMIUM_AFFIRMATIONS_POOL: string[] = [
  'I am safe, loved, and never alone.',
  "I can tell a trusted grown-up anything \u2014 I will never be in trouble for telling the truth.",
  'My feelings are real and they matter.',
  'I am allowed to say no when something feels wrong.',
  'Asking for help is a sign of strength.',
  'I am curious and I love to learn new things.',
  'Mistakes help me grow \u2014 I keep trying.',
  'I am kind, and kindness makes the world better.',
  'My body belongs to me and no one else.',
  'I can handle hard things, one step at a time.',
  'I am enough exactly as I am today.',
  'It is okay to feel sad, angry, or scared \u2014 feelings always pass.',
  'I treat others the way I want to be treated.',
  'I am brave even when I feel nervous.',
  'I notice when someone needs help, and I speak up.',
  'I can disagree and still be kind.',
  'Sleep, food, and play keep me healthy and happy.',
  'I know the difference between a safe secret and an unsafe one.',
  'I am proud of how far I have come.',
  'I can make new friends by being myself.',
  'I choose to be honest even when it is hard.',
  'I have a voice and my words matter.',
  'I am grateful for the people who love me.',
  'I can take three deep breaths and feel calmer.',
  'I look after the planet because it looks after me.',
  "I celebrate other people's wins \u2014 there is enough success for everyone.",
  'I know who to call or find if I ever feel unsafe.',
  'I am creative and full of great ideas.',
  "I respect other people's feelings and space.",
  'Every day is a chance to be a little better than yesterday.',
];

/**
 * Returns the appropriate affirmation text based on the user's tier:
 * - guest / free  → always returns GUEST_STATIC_AFFIRMATION
 * - premium       → rotates through PREMIUM_AFFIRMATIONS_POOL by day-of-year
 */
export function getDailyAffirmationForTier(
  tier: 'guest' | 'free' | 'premium',
  ageGroups: Array<'4-6' | '7-9' | '10-12'>
): Affirmation {
  if (tier !== 'premium') {
    return GUEST_STATIC_AFFIRMATION;
  }

  const year = new Date().getFullYear();
  const dayOfYear = Math.ceil(
    (Date.now() - new Date(year, 0, 1).getTime()) / 86400000
  );
  const text = PREMIUM_AFFIRMATIONS_POOL[(dayOfYear - 1) % PREMIUM_AFFIRMATIONS_POOL.length];

  // Try to find the matching record in AFFIRMATIONS for category + parentPrompt
  const match = AFFIRMATIONS.find((a) => a.text === text);
  if (match) return match;

  // Fallback synthetic record when text isn't in AFFIRMATIONS (indices 0, 6-9, 11-29)
  return {
    id: `daily-${dayOfYear}`,
    text,
    ageGroups: ageGroups.length > 0 ? ageGroups : ['4-6', '7-9', '10-12'],
    category: 'Resilience & Growth Mindset',
    parentPrompt: "Talk about this affirmation together with your child today.",
    isPremium: true,
  };
}
